import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  Divider,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  TableSortLabel,
} from "@mui/material";
import OrbLoader from "../components/common/OrbLoader";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import { selectUserDb } from "../store/slices/session/sessionSelector";
import { useGetCampaignByIdQuery } from "../store/apis/campaignApi";
import { useGetInternalCampaignMetricsQuery } from "../store/apis/internalCampaignsMetricsApi";
import { setToastMessage } from "../store/slices/app/appSlice";
import { components } from "../types/api";
import MetricsTimelineChart, {
  type MetricDataPoint,
} from "../components/card/MetricsTimelineChart";
import { exportMetricsToCSV } from "../utils/exportUtils";
import { exportCreativeMetricsToCSV } from "../utils/exportCreativeMetricsUtils";
import { exportPublisherMetricsToCSV } from "../utils/exportPublisherMetricsUtils";
import { getPlatformDisplayName } from "../utils/iconUtils";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useGetCreativeMetricsQuery } from "../store/apis/creativeMetricsApi";
import { useGetPublisherMetricsQuery } from "../store/apis/publisherMetricsApi";
import { getCurrencySymbol } from "../utils/currencyUtils";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import { createNumberFormat, formatFixed } from "../utils/numberFormat";

// Define a type for the internal campaign that includes the properties we need
type InternalCampaignWithStatus = components["schemas"]["InternalCampaign"] & {
  status?: string;
  platform?: string;
};

const mapDailyMetricsToTimelineData = (
  dailyData: components["schemas"]["DailyMetrics"][] = []
): MetricDataPoint[] =>
  dailyData.map((day) => ({
    date: day.date,
    ctr: day.ctr ?? 0,
    cpc: day.cpc ?? 0,
    cpm: day.cpm ?? 0,
    impressions: day.impressions ?? 0,
    clicks: day.clicks ?? 0,
    cost: day.cost ?? 0,
    completeViews: day.completeViews ?? 0,
  }));

const InternalCampaignDetails: React.FC = () => {
  // State for date filters
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { internalCampaignId, parentCampaignId } = useParams<{
    internalCampaignId: string;
    parentCampaignId: string;
  }>();
  const sidebarCollapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);

  // Fetch internal campaign details from parent campaign
  const {
    data: campaignDetailResponse,
    error: campaignDetailError,
    isError: isCampaignDetailError,
    isLoading: isLoadingCampaignDetail,
  } = useGetCampaignByIdQuery(
    {
      campaignId: parentCampaignId || "",
    },
    {
      skip: !parentCampaignId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Log errors for debugging
  useEffect(() => {
    if (isCampaignDetailError) {
      console.error("Error fetching campaign details:", campaignDetailError);
    }
  }, [isCampaignDetailError, campaignDetailError]);

  // Fetch metrics for the internal campaign (includes daily data and summary)
  const {
    data: metricsData,
    error: metricsError,
    isLoading: isLoadingMetrics,
  } = useGetInternalCampaignMetricsQuery(
    {
      path: {
        internalCampaignId: internalCampaignId || "",
      },
      query: startDate && endDate ? { startDate, endDate } : undefined,
    },
    {
      skip: !internalCampaignId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Fetch metrics for the creative metrics
  const {
    data: creativeMetricsData,
    error: creativeMetricsError,
    isLoading: isLoadingCreativeMetrics,
  } = useGetCreativeMetricsQuery(
    {
      path: {
        internalCampaignId: internalCampaignId || "",
      },
      query: {
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      },
    },
    {
      skip: !internalCampaignId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Fetch metrics for the publisher metrics
  const {
    data: publisherMetricsData,
    error: publisherMetricsError,
    isLoading: isLoadingPublisherMetrics,
  } = useGetPublisherMetricsQuery(
    {
      path: {
        internalCampaignId: internalCampaignId || "",
      },
      query: {
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      },
    },
    {
      skip: !internalCampaignId,
      refetchOnMountOrArgChange: true,
    }
  );

  type Order = "asc" | "desc";

  // Creative metrics sorting state
  const [creativeOrder, setCreativeOrder] = useState<Order>("desc");
  const [creativeOrderBy, setCreativeOrderBy] = useState<string>("impressions");
  const handleCreativeRequestSort = (property: string) => {
    const isAsc = creativeOrderBy === property && creativeOrder === "asc";
    setCreativeOrder(isAsc ? "desc" : "asc");
    setCreativeOrderBy(property);
  };
  const sortedCreatives = React.useMemo(() => {
    if (!creativeMetricsData?.creatives) return [];
    return [...creativeMetricsData.creatives].sort(
      (
        a: components["schemas"]["CreativeMetricsSummary"],
        b: components["schemas"]["CreativeMetricsSummary"]
      ) => {
        const aVal =
          a[
            creativeOrderBy as keyof components["schemas"]["CreativeMetricsSummary"]
          ] ?? 0;
        const bVal =
          b[
            creativeOrderBy as keyof components["schemas"]["CreativeMetricsSummary"]
          ] ?? 0;
        if (bVal < aVal) return creativeOrder === "asc" ? 1 : -1;
        if (bVal > aVal) return creativeOrder === "asc" ? -1 : 1;
        return 0;
      }
    );
  }, [creativeMetricsData?.creatives, creativeOrder, creativeOrderBy]);

  // Publisher metrics sorting state
  const [publisherOrder, setPublisherOrder] = useState<Order>("desc");
  const [publisherOrderBy, setPublisherOrderBy] =
    useState<string>("impressions");
  const handlePublisherRequestSort = (property: string) => {
    const isAsc = publisherOrderBy === property && publisherOrder === "asc";
    setPublisherOrder(isAsc ? "desc" : "asc");
    setPublisherOrderBy(property);
  };
  const sortedPublishers = React.useMemo(() => {
    if (!publisherMetricsData?.publishers) return [];
    return [...publisherMetricsData.publishers].sort(
      (
        a: components["schemas"]["PublisherMetricsSummary"],
        b: components["schemas"]["PublisherMetricsSummary"]
      ) => {
        const aVal =
          a[
            publisherOrderBy as keyof components["schemas"]["PublisherMetricsSummary"]
          ] ?? 0;
        const bVal =
          b[
            publisherOrderBy as keyof components["schemas"]["PublisherMetricsSummary"]
          ] ?? 0;
        if (bVal < aVal) return publisherOrder === "asc" ? 1 : -1;
        if (bVal > aVal) return publisherOrder === "asc" ? -1 : 1;
        return 0;
      }
    );
  }, [publisherMetricsData?.publishers, publisherOrder, publisherOrderBy]);

  // Find the internal campaign in the parent campaign's internal campaigns
  // Using InternalCampaignWithStatus type to handle properties that exist at runtime
  const internalCampaign =
    campaignDetailResponse?.data?.internalCampaigns?.find(
      (campaign) => campaign.id === internalCampaignId
    ) as InternalCampaignWithStatus;

  // Helper function to format date to YYYY-MM-DD for input fields
  const formatDateForInput = useCallback(
    (dateString: string | undefined): string => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    },
    []
  );

  // Effect to initialize date filters with campaign dates when data is loaded
  useEffect(() => {
    if (metricsData) {
      // If we have campaign dates from the API and no filters are set,
      // initialize the filters with the campaign's start and end dates
      if (
        !startDate &&
        !endDate &&
        metricsData.startDate &&
        metricsData.endDate
      ) {
        setStartDate(formatDateForInput(metricsData.startDate));
        setEndDate(formatDateForInput(metricsData.endDate));
      }
    }
  }, [metricsData, startDate, endDate, formatDateForInput]);

  // Handle campaign detail fetch errors
  useEffect(() => {
    if (campaignDetailError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.detailError", {
            defaultValue: "Failed to load campaign details",
          }),
          severity: "error",
        })
      );
    }
  }, [campaignDetailError, dispatch, t]);

  // Handle metrics fetch errors
  useEffect(() => {
    if (metricsError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.metricsError", {
            defaultValue: "Failed to load campaign metrics",
          }),
          severity: "error",
        })
      );
    }
  }, [metricsError, dispatch, t]);

  // Handle creative metrics errors
  useEffect(() => {
    if (creativeMetricsError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.creativeMetricsError", {
            defaultValue: "Failed to load creative metrics",
          }),
          severity: "error",
        })
      );
    }
  }, [creativeMetricsError, dispatch, t]);

  // Handle publisher metrics errors
  useEffect(() => {
    if (publisherMetricsError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.publisherMetricsError", {
            defaultValue: "Failed to load publisher metrics",
          }),
          severity: "error",
        })
      );
    }
  }, [publisherMetricsError, dispatch, t]);

  // We only display metrics that come directly from the backend

  // Determine if we're loading
  const isLoading = isLoadingCampaignDetail || isLoadingMetrics;

  // Handle errors
  if (campaignDetailError) {
    dispatch(
      setToastMessage({
        message: t("campaigns.detailError", {
          defaultValue: "Failed to load campaign details",
        }),
        severity: "error",
      })
    );
  }

  if (metricsError) {
    dispatch(
      setToastMessage({
        message: t("campaigns.metricsError", {
          defaultValue: "Failed to load internal campaign metrics",
        }),
        severity: "error",
      })
    );
  }

  // handleBack function is defined above to be accessible for both admin and non-admin views

  // Handle metrics data export
  const handleExportMetrics = () => {
    if (!metricsData?.dailyData) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    // Transform the data to match the CampaignMetric interface
    const formattedData = metricsData.dailyData.map((day) => ({
      date: day.date,
      impressions: day.impressions || 0,
      clicks: day.clicks || 0,
      ctr: day.ctr || 0,
      cpm: day.cpm || 0,
      cpc: day.cpc || 0,
      cost: day.cost || 0,
      cpv: day.cpv || 0,
      vtr: day.vtr || 0,
      dailyInvestment: day.cost || 0, // Use cost as dailyInvestment
      dailyImpressions: day.impressions || 0, // Use impressions as dailyImpressions
      completeViews: day.completeViews || 0,
      dailyCTR: day.ctr || 0, // Use ctr as dailyCTR
      conversions: day.conversions || 0,
    }));

    exportMetricsToCSV(
      formattedData,
      internalCampaign?.displayName || "export",
      t,
      dispatch
    );
  };

  // Handle creative metrics data export
  const handleExportCreativeMetrics = () => {
    if (!creativeMetricsData?.creatives) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    exportCreativeMetricsToCSV(
      creativeMetricsData.creatives,
      internalCampaign?.displayName || "export",
      t,
      dispatch,
      creativeMetricsData?.currency_enum || "EUR"
    );
  };

  // Handle publisher metrics data export
  const handleExportPublisherMetrics = () => {
    if (!publisherMetricsData?.publishers) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    exportPublisherMetricsToCSV(
      publisherMetricsData.publishers,
      internalCampaign?.displayName || "export",
      t,
      dispatch,
      getCurrencySymbol(creativeMetricsData?.currency_enum || "EUR")
    );
  };

  const formatNumber = (num?: number) => {
    if (num === undefined) return "0";
    return createNumberFormat().format(Math.round(num));
  };

  const formatCurrency = (num?: number) => {
    if (num === undefined) return formatFixed(0, 2) + " $";
    return createNumberFormat({
      style: "currency",
      currency: metricsData?.currency_enum || "USD",
      minimumFractionDigits: 2,
    }).format(num);
  };

  const formatPercentage = (num?: number) => {
    if (num === undefined) return "0,00 %";
    return createNumberFormat({
      style: "percent",
      minimumFractionDigits: 2,
    }).format(num);
  };

  // Reusable MetricCard component
  const MetricCard = ({
    value,
    label,
  }: {
    value: React.ReactNode;
    label: string;
  }) => (
    <Box
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.04)",
        borderRadius: 2,
        border: `1px solid ${theme.palette.neutral[200]}`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
      }}
    >
      <Typography
        variant="h3"
        sx={{ color: theme.palette.primary.main, fontWeight: 700 }}
      >
        {value}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.primary.main,
          fontFamily: "Nunito",
          fontWeight: 400,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  // Check if user is admin
  const isAdmin = userDb?.role === "ADMIN";

  const handleBack = () => {
    // Navigate back to campaigns page with the parent campaign ID as state
    // This will be used to reselect the parent campaign
    navigate("/campaigns", { state: { selectedCampaignId: parentCampaignId } });
  };

  return (
    <>
      <Box
        sx={{
          p: 3,
          ml: { xs: 0, md: sidebarCollapsed ? "80px" : "240px" },
          width: {
            xs: "100%",
            md: sidebarCollapsed ? "calc(100% - 80px)" : "calc(100% - 240px)",
          },
          transition: "margin 0.2s ease-in-out, width 0.2s ease-in-out",
          boxSizing: "border-box",
        }}
      >
        <PageBreadcrumbs />

        {!isAdmin ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "70vh",
              gap: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 500, textAlign: "center" }}
            >
              {t("common.dataNotAvailable", {
                defaultValue: "Data not available",
              })}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              variant="contained"
              onClick={handleBack}
              sx={{
                borderRadius: 50,
                textTransform: "none",
                fontFamily: "Nunito",
                px: 3,
                py: 1,
              }}
            >
              {t("campaigns.back")}
            </Button>
          </Box>
        ) : (
          <>
            {/* Admin header with back button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                onClick={handleBack}
                sx={{
                  mr: 2,
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  borderColor: theme.palette.grey[200],
                  color: theme.palette.grey[700],
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: "transparent",
                  },
                }}
              >
                {t("campaigns.back")}
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {internalCampaign?.displayName || ""}
                </Typography>
                {internalCampaign?.platform && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: "primary.light",
                      color: "primary.contrastText",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {getPlatformDisplayName(internalCampaign.platform)}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Admin content */}
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <OrbLoader
                  size="large"
                  label={t("common.loading", { defaultValue: "Cargando…" })}
                />
              </Box>
            ) : (
              <>
                {/* Metrics */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, fontFamily: "Montserrat" }}
                    >
                      {t("campaigns.metrics", { defaultValue: "Metrics" })}
                    </Typography>

                    {/* Date Filter */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TextField
                        label={t("common.startDate", {
                          defaultValue: "Start Date",
                        })}
                        type="date"
                        size="small"
                        value={startDate || ""}
                        onChange={(e) => {
                          const newStart = e.target.value;
                          setStartDate(newStart);
                          // If endDate exists and is before new start, align it to new start
                          if (
                            endDate &&
                            new Date(endDate) < new Date(newStart)
                          ) {
                            setEndDate(newStart);
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: formatDateForInput(metricsData?.startDate),
                          max:
                            endDate || formatDateForInput(metricsData?.endDate),
                        }}
                        sx={{ width: 150 }}
                      />

                      <TextField
                        label={t("common.endDate", {
                          defaultValue: "End Date",
                        })}
                        type="date"
                        size="small"
                        value={endDate || ""}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min:
                            startDate ||
                            formatDateForInput(metricsData?.startDate),
                          max: formatDateForInput(metricsData?.endDate),
                        }}
                        sx={{ width: 150 }}
                        disabled={!startDate}
                      />

                      <IconButton
                        onClick={() => {
                          setStartDate("");
                          setEndDate("");
                        }}
                        size="small"
                        color="primary"
                        title={t("common.clearFilters", {
                          defaultValue: "Clear Filters",
                        })}
                      >
                        <ClearIcon />
                      </IconButton>

                      {/* Export Button */}
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportMetrics}
                        sx={{
                          ml: 1,
                          height: 36,
                          textTransform: "none",
                          fontWeight: 500,
                        }}
                      >
                        {t("campaigns.exportMetrics", {
                          defaultValue: "Export Data",
                        })}
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {/* Metrics cards */}
                  {metricsData?.summary && (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 3,
                        "& > *": {
                          flex: "1 1 calc(20% - 24px)", // 5 cards per row (24px is the gap * (5-1)/5)
                          minWidth: "180px",
                        },
                      }}
                    >
                      {/* Cost */}
                      <MetricCard
                        value={formatCurrency(metricsData.summary.totalCost)}
                        label={t("campaigns.cost", {
                          defaultValue: "Total Cost",
                        })}
                      />

                      {/* Impressions */}
                      <MetricCard
                        value={formatNumber(
                          metricsData.summary.totalImpressions
                        )}
                        label={t("campaigns.impressions", {
                          defaultValue: "Impressions",
                        })}
                      />

                      {/* Clicks */}
                      <MetricCard
                        value={formatNumber(metricsData.summary.totalClicks)}
                        label={t("campaigns.clicks", {
                          defaultValue: "Clicks",
                        })}
                      />

                      {/* CPM */}
                      <MetricCard
                        value={formatCurrency(metricsData.summary.avgCPM)}
                        label={t("campaigns.cpm", { defaultValue: "CPM" })}
                      />

                      {/* CPC */}
                      <MetricCard
                        value={formatCurrency(metricsData.summary.avgCPC)}
                        label={t("campaigns.cpc", { defaultValue: "CPC" })}
                      />

                      {/* CTR */}
                      <MetricCard
                        value={formatPercentage(metricsData.summary.avgCTR)}
                        label={t("campaigns.ctr", { defaultValue: "CTR" })}
                      />

                      {/* Conversions */}
                      <MetricCard
                        value={formatNumber(
                          metricsData.summary.totalConversions || 0
                        )}
                        label={t("campaigns.totalConversions", {
                          defaultValue: "Conversions",
                        })}
                      />

                      {/* Conversion Rate */}
                      <MetricCard
                        value={formatPercentage(
                          metricsData.summary.conversionRate || 0
                        )}
                        label={t("campaigns.conversionRate", {
                          defaultValue: "Conversion Rate",
                        })}
                      />

                      {/* CPA */}
                      <MetricCard
                        value={formatCurrency(metricsData.summary.cpa || 0)}
                        label={t("campaigns.cpa", {
                          defaultValue: "CPA",
                        })}
                      />

                      {/* Frequency */}
                      <MetricCard
                        value={formatFixed(
                          metricsData.summary.frequency ?? 0,
                          2
                        )}
                        label={t("campaigns.frequency", {
                          defaultValue: "Frequency",
                        })}
                      />
                    </Box>
                  )}

                  {/* Metrics Timeline Chart */}
                  {metricsData?.dailyData &&
                  metricsData.dailyData.length > 0 ? (
                    <Box sx={{ mt: 4 }}>
                      <MetricsTimelineChart
                        title={t("campaigns.metricsOverTime", {
                          defaultValue: "Metrics Over Time",
                        })}
                        subtitle={t("campaigns.clickMetricsToCompare", {
                          defaultValue:
                            "Click on metrics to compare or select 'All' to see accumulated data",
                        })}
                        data={mapDailyMetricsToTimelineData(
                          metricsData.dailyData
                        )}
                        isLoading={isLoadingMetrics}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mt: 4,
                        p: 3,
                        textAlign: "center",
                        bgcolor: theme.palette.background.default,
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        {t("campaigns.noMetricsData", {
                          defaultValue:
                            "No metrics data available for the selected time period. Data will appear here once the campaign starts generating metrics.",
                        })}
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Creative Metrics Table */}
                {creativeMetricsData && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, fontFamily: "Montserrat" }}
                      >
                        {t("campaigns.creativeMetrics", {
                          defaultValue: "Creative Metrics",
                        })}
                      </Typography>

                      {/* Export Button */}
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportCreativeMetrics}
                        sx={{
                          height: 36,
                          textTransform: "none",
                          fontWeight: 500,
                        }}
                        disabled={
                          !creativeMetricsData?.creatives ||
                          creativeMetricsData.creatives.length === 0
                        }
                      >
                        {t("campaigns.exportCreativeMetrics", {
                          defaultValue: "Export Data",
                        })}
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {isLoadingCreativeMetrics ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100px",
                        }}
                      >
                        <OrbLoader size="medium" />
                      </Box>
                    ) : creativeMetricsData.creatives &&
                      creativeMetricsData.creatives.length > 0 ? (
                      <TableContainer
                        component={Paper}
                        sx={{
                          borderRadius: 2,
                          maxHeight: 600,
                          overflow: "auto",
                        }}
                      >
                        <Table
                          stickyHeader
                          sx={{ minWidth: 650 }}
                          aria-label="creative metrics table"
                        >
                          <TableHead
                            sx={{
                              backgroundColor: theme.palette.neutral["200"],
                            }}
                          >
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={creativeOrderBy === "creativeName"}
                                  direction={
                                    creativeOrderBy === "creativeName"
                                      ? creativeOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handleCreativeRequestSort("creativeName")
                                  }
                                >
                                  {t("campaigns.creative", {
                                    defaultValue: "Creative",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  width: 80,
                                  textAlign: "center",
                                }}
                              >
                                <TableSortLabel
                                  active={creativeOrderBy === "enabled"}
                                  direction={
                                    creativeOrderBy === "enabled"
                                      ? creativeOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handleCreativeRequestSort("enabled")
                                  }
                                >
                                  {t("campaigns.enabled", {
                                    defaultValue: "Enabled",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={creativeOrderBy === "impressions"}
                                  direction={
                                    creativeOrderBy === "impressions"
                                      ? creativeOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handleCreativeRequestSort("impressions")
                                  }
                                >
                                  {t("campaigns.impressions", {
                                    defaultValue: "Impressions",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={creativeOrderBy === "clicks"}
                                  direction={
                                    creativeOrderBy === "clicks"
                                      ? creativeOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handleCreativeRequestSort("clicks")
                                  }
                                >
                                  {t("campaigns.clicks", {
                                    defaultValue: "Clicks",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={creativeOrderBy === "ctr"}
                                  direction={
                                    creativeOrderBy === "ctr"
                                      ? creativeOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handleCreativeRequestSort("ctr")
                                  }
                                >
                                  {t("campaigns.ctr", { defaultValue: "CTR" })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={creativeOrderBy === "cpm"}
                                  direction={
                                    creativeOrderBy === "cpm"
                                      ? creativeOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handleCreativeRequestSort("cpm")
                                  }
                                >
                                  {t("campaigns.cpm", { defaultValue: "CPM" })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={creativeOrderBy === "cpc"}
                                  direction={
                                    creativeOrderBy === "cpc"
                                      ? creativeOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handleCreativeRequestSort("cpc")
                                  }
                                >
                                  {t("campaigns.cpc", { defaultValue: "CPC" })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={creativeOrderBy === "cost"}
                                  direction={
                                    creativeOrderBy === "cost"
                                      ? creativeOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handleCreativeRequestSort("cost")
                                  }
                                >
                                  {t("campaigns.cost", {
                                    defaultValue: "Cost",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedCreatives.map(
                              (
                                creative: components["schemas"]["CreativeMetricsSummary"]
                              ) => (
                                <TableRow
                                  key={creative.creativeId}
                                  hover
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                    transition: "background-color 0.3s ease",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.neutral["100"],
                                    },
                                  }}
                                >
                                  <TableCell component="th" scope="row">
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                      }}
                                    >
                                      {creative.thumbnailUrl && (
                                        <Box
                                          component="img"
                                          src={creative.thumbnailUrl}
                                          alt={creative.creativeName}
                                          sx={{
                                            width: 60,
                                            height: 60,
                                            objectFit: "cover",
                                            borderRadius: 1,
                                          }}
                                          onError={(e) => {
                                            // Hide the image on error
                                            e.currentTarget.style.display =
                                              "none";
                                          }}
                                        />
                                      )}
                                      <Box>
                                        <Typography variant="subtitle2">
                                          {creative.creativeName}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {creative.headline}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    <Switch
                                      checked={creative.enabled === true}
                                      disabled={true}
                                      size="small"
                                      color={
                                        creative.enabled === true
                                          ? "success"
                                          : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {formatNumber(creative.impressions)}
                                  </TableCell>
                                  <TableCell>
                                    {formatNumber(creative.clicks)}
                                  </TableCell>
                                  <TableCell>
                                    {formatPercentage(creative.ctr)}
                                  </TableCell>
                                  <TableCell>
                                    {formatCurrency(creative.cpm)}
                                  </TableCell>
                                  <TableCell>
                                    {formatCurrency(creative.cpc)}
                                  </TableCell>
                                  <TableCell>
                                    {formatCurrency(creative.cost)}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                          {t("campaigns.noCreativeMetrics", {
                            defaultValue: "No creative metrics available",
                          })}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}

                {/* Publisher Metrics Table */}
                {publisherMetricsData && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, fontFamily: "Montserrat" }}
                      >
                        {t("campaigns.publisherMetrics", {
                          defaultValue: "Publisher Metrics",
                        })}
                      </Typography>

                      {/* Export Button */}
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportPublisherMetrics}
                        sx={{
                          height: 36,
                          textTransform: "none",
                          fontWeight: 500,
                        }}
                        disabled={
                          !publisherMetricsData?.publishers ||
                          publisherMetricsData.publishers.length === 0
                        }
                      >
                        {t("campaigns.exportPublisherMetrics", {
                          defaultValue: "Export Data",
                        })}
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {isLoadingPublisherMetrics ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100px",
                        }}
                      >
                        <OrbLoader size="medium" />
                      </Box>
                    ) : publisherMetricsData.publishers &&
                      publisherMetricsData.publishers.length > 0 ? (
                      <TableContainer
                        component={Paper}
                        sx={{
                          borderRadius: 2,
                          maxHeight: 600,
                          overflow: "auto",
                        }}
                      >
                        <Table
                          stickyHeader
                          sx={{ minWidth: 650 }}
                          aria-label="publisher metrics table"
                        >
                          <TableHead
                            sx={{
                              backgroundColor: theme.palette.neutral["200"],
                            }}
                          >
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={publisherOrderBy === "publisherName"}
                                  direction={
                                    publisherOrderBy === "publisherName"
                                      ? publisherOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handlePublisherRequestSort("publisherName")
                                  }
                                >
                                  {t("campaigns.publisher", {
                                    defaultValue: "Publisher",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={publisherOrderBy === "platform"}
                                  direction={
                                    publisherOrderBy === "platform"
                                      ? publisherOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handlePublisherRequestSort("platform")
                                  }
                                >
                                  {t("campaigns.platform", {
                                    defaultValue: "Platform",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={publisherOrderBy === "impressions"}
                                  direction={
                                    publisherOrderBy === "impressions"
                                      ? publisherOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handlePublisherRequestSort("impressions")
                                  }
                                >
                                  {t("campaigns.impressions", {
                                    defaultValue: "Impressions",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={publisherOrderBy === "clicks"}
                                  direction={
                                    publisherOrderBy === "clicks"
                                      ? publisherOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handlePublisherRequestSort("clicks")
                                  }
                                >
                                  {t("campaigns.clicks", {
                                    defaultValue: "Clicks",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={publisherOrderBy === "ctr"}
                                  direction={
                                    publisherOrderBy === "ctr"
                                      ? publisherOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handlePublisherRequestSort("ctr")
                                  }
                                >
                                  {t("campaigns.ctr", { defaultValue: "CTR" })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={publisherOrderBy === "cpm"}
                                  direction={
                                    publisherOrderBy === "cpm"
                                      ? publisherOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handlePublisherRequestSort("cpm")
                                  }
                                >
                                  {t("campaigns.cpm", { defaultValue: "CPM" })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                <TableSortLabel
                                  active={publisherOrderBy === "cost"}
                                  direction={
                                    publisherOrderBy === "cost"
                                      ? publisherOrder
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handlePublisherRequestSort("cost")
                                  }
                                >
                                  {t("campaigns.cost", {
                                    defaultValue: "Cost",
                                  })}
                                </TableSortLabel>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedPublishers.map(
                              (
                                publisher: components["schemas"]["PublisherMetricsSummary"]
                              ) => (
                                <TableRow
                                  key={publisher.publisherId}
                                  hover
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                    transition: "background-color 0.3s ease",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.neutral["100"],
                                    },
                                  }}
                                >
                                  <TableCell>
                                    <Box>
                                      <Typography variant="subtitle2">
                                        {publisher.publisherName}
                                      </Typography>
                                      {publisher.sectionName && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {publisher.sectionName}
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    {publisher.platform
                                      ? getPlatformDisplayName(
                                          publisher.platform
                                        )
                                      : t("common.notAvailable", {
                                          defaultValue: "N/A",
                                        })}
                                  </TableCell>
                                  <TableCell>
                                    {formatNumber(publisher.impressions)}
                                  </TableCell>
                                  <TableCell>
                                    {formatNumber(publisher.clicks)}
                                  </TableCell>
                                  <TableCell>
                                    {formatPercentage(publisher.ctr)}
                                  </TableCell>
                                  <TableCell>
                                    {formatCurrency(publisher.cpm)}
                                  </TableCell>
                                  <TableCell>
                                    {formatCurrency(publisher.cost)}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                          {t("campaigns.noPublisherMetrics", {
                            defaultValue: "No publisher metrics available",
                          })}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default InternalCampaignDetails;
